import React from 'react';
import '../../styles/prepare-up-interviews.css';

/*
 * InterviewAvatar — the AI interviewer's on-screen presence: a restrained,
 * head-and-shoulders portrait (no cartoon features) in five personas. It only
 * changes how the interviewer looks; the voice comes from the server.
 */
const LOOKS = {
  professional: { skin: '#e2b89c', hair: '#2b2622', hairShape: 'short', top: '#1f3a5f', shirt: '#f4f6f9', tie: null, collar: 'open' },
  friendly: { skin: '#c99274', hair: '#4a3426', hairShape: 'long', top: '#56604f', shirt: '#e9e4da', tie: null, collar: 'crew' },
  formal: { skin: '#f0c9ad', hair: '#1c1b1d', hairShape: 'neat', top: '#2a2e35', shirt: '#ffffff', tie: '#34476b', collar: 'open' },
  executive: { skin: '#e3bc9f', hair: '#c4c7cc', hairShape: 'neat', top: '#3a3f47', shirt: '#f7f7f5', tie: '#5e2f38', collar: 'open' },
  technical: { skin: '#8f5d40', hair: '#1f1a17', hairShape: 'curly', top: '#44546a', shirt: '#2f3a48', tie: null, collar: 'zip', glasses: true },
};

const Hair = ({ shape, color }) => {
  if (shape === 'long') {
    return <path d="M60 58 C58 30 78 18 100 18 C122 18 142 30 140 58 L144 104 C136 96 132 84 132 70 C120 60 82 60 68 70 C68 84 64 96 56 104 Z" fill={color} />;
  }
  if (shape === 'curly') {
    return (
      <g fill={color}>
        <path d="M64 62 C60 34 80 20 100 20 C120 20 140 34 136 62 C128 50 116 44 100 44 C84 44 72 50 64 62 Z" />
        {[70, 82, 94, 106, 118, 130].map((x, i) => <circle key={x} cx={x} cy={i % 2 ? 30 : 36} r="9" />)}
      </g>
    );
  }
  const neat = shape === 'neat';
  return <path d={neat
    ? 'M64 64 C62 36 80 22 100 22 C122 22 138 36 136 64 C130 50 120 42 100 42 C86 42 74 46 64 64 Z'
    : 'M63 66 C60 36 78 20 101 20 C124 20 140 38 137 66 C132 52 118 40 98 41 C84 42 72 50 63 66 Z'} fill={color} />;
};

const InterviewAvatar = ({ persona = 'professional', speaking = false, size = 'lg', label }) => {
  const L = LOOKS[persona] || LOOKS.professional;
  return (
    <div className={`pu-avatar-portrait ${size}${speaking ? ' speaking' : ''}`} data-persona={persona}>
      <svg viewBox="0 0 200 200" role="img" aria-label={label || 'AI interviewer'}>
        {/* shoulders + jacket */}
        <path d="M22 200 C26 158 52 138 84 132 L116 132 C148 138 174 158 178 200 Z" fill={L.top} />
        {L.collar === 'crew' && <path d="M80 133 C88 146 112 146 120 133 L116 132 L84 132 Z" fill={L.shirt} />}
        {L.collar === 'zip' && <>
          <path d="M84 132 L100 158 L116 132 Z" fill={L.shirt} />
          <line x1="100" y1="158" x2="100" y2="200" stroke="rgba(255,255,255,.25)" strokeWidth="1.5" />
        </>}
        {L.collar === 'open' && <>
          <path d="M84 132 L100 170 L116 132 Z" fill={L.shirt} />
          <path d="M84 132 L94 176 L76 150 Z" fill="rgba(0,0,0,.18)" />
          <path d="M116 132 L106 176 L124 150 Z" fill="rgba(0,0,0,.18)" />
          {L.tie && <path d="M97 140 L103 140 L106 176 L100 184 L94 176 Z" fill={L.tie} />}
        </>}
        {/* neck + head */}
        <path d="M88 112 L112 112 L114 136 C106 142 94 142 86 136 Z" fill={L.skin} />
        <path d="M88 124 C96 130 104 130 112 124 L112 118 L88 118 Z" fill="rgba(0,0,0,.08)" />
        <ellipse cx="100" cy="76" rx="35" ry="42" fill={L.skin} />
        <ellipse cx="65" cy="80" rx="5" ry="9" fill={L.skin} />
        <ellipse cx="135" cy="80" rx="5" ry="9" fill={L.skin} />
        <Hair shape={L.hairShape} color={L.hair} />
        {L.glasses && (
          <g fill="none" stroke="#1d232c" strokeWidth="2.2">
            <rect x="76" y="72" width="20" height="13" rx="5" />
            <rect x="104" y="72" width="20" height="13" rx="5" />
            <line x1="96" y1="77" x2="104" y2="77" />
          </g>
        )}
      </svg>
    </div>
  );
};

export default InterviewAvatar;
